import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatCount,
  formatDuration,
  getContentById,
  getRelatedContents,
  typeLabels,
} from "@/lib/content";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getContentById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playerOpen, setPlayerOpen] = useState(false);
  const [toast, setToast] = useState("");

  useSEO({
    title: item ? `${item.title} - 羞羞网站` : "内容不存在 - 羞羞网站",
    description: item ? `${item.summary} ${typeLabels[item.type]}内容，尽在羞羞网站。` : "该内容可能已被移除。",
    keywords: item ? `羞羞网站,${typeLabels[item.type]},${item.tags.join(",")}` : "羞羞网站",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="w-full bg-background-50 min-h-screen">
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center">
            <i className="ri-ghost-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-2xl text-foreground-950 mt-5">内容走丢了</h1>
          <p className="text-sm text-foreground-600 mt-2 max-w-md">
            这条内容可能被移除了，或者链接不太对。回到首页看看别的吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-5-line" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const favorite = isFavorite(item.id);
  const related = getRelatedContents(item, 6);

  const handleFavorite = () => {
    const next = toggleFavorite(item.id);
    setToast(next ? "已加入我的收藏" : "已从收藏中移除");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-8 pt-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${item.type}`} className="hover:text-primary-600">
              {typeLabels[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-900 line-clamp-1">{item.title}</span>
          </nav>
        </div>

        <article className="w-full px-4 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${item.type}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium"
              >
                {typeLabels[item.type]}
              </Link>
              {item.tags.map((t) => (
                <span key={t} className="text-xs text-foreground-500">#{t}</span>
              ))}
            </div>

            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug">
              {item.title}
            </h1>

            <div className="flex flex-wrap items-center gap-5 mt-4 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" /> {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" /> {formatCount(item.views)} 浏览
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-3-line" /> {formatCount(item.likes)}
              </span>
            </div>

            {item.type === "video" ? (
              <button
                type="button"
                onClick={() => setPlayerOpen(true)}
                className="group relative w-full rounded-lg overflow-hidden mt-7 aspect-video bg-foreground-950 text-left"
              >
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - ${typeLabels[item.type]}`}
                  className="w-full h-full object-cover object-top opacity-80 group-hover:opacity-70 transition-opacity"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 rounded-full bg-primary-500 text-background-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-3xl" />
                  </span>
                </span>
                {item.duration > 0 && (
                  <span className="absolute bottom-4 right-4 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs">
                    时长 {formatDuration(item.duration)}
                  </span>
                )}
                <span className="absolute bottom-4 left-4 text-background-50/90 text-xs">
                  演示播放器 · 点击查看说明
                </span>
              </button>
            ) : (
              <div className="rounded-lg overflow-hidden mt-7 h-64 md:h-80 bg-background-100">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - ${typeLabels[item.type]}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap border ${
                  favorite
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-800 border-background-300 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {favorite ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap border bg-background-50 text-foreground-800 border-background-300 hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
              <Link
                to={`/category/${item.type}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap text-accent-700 bg-accent-100 hover:bg-accent-200"
              >
                <i className="ri-price-tag-3-line" />
                更多{typeLabels[item.type]}
              </Link>
            </div>

            <div className="mt-9">
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">
                {item.type === "joke" ? "段子正文" : item.type === "interview" ? "访谈内容" : "内容简介"}
              </h2>
              <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 text-sm md:text-base text-foreground-800 leading-loose whitespace-pre-line">
                {item.body}
              </div>
              <p className="text-xs text-foreground-500 mt-4">
                注：本站内容均为前端演示数据，仅供浏览体验使用。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-magic-line text-primary-500" />
                相关内容推荐
              </h2>
              <div className="flex flex-col gap-4">
                {related.map((rec) => (
                  <Link
                    key={rec.id}
                    to={`/detail/${rec.id}`}
                    className="group flex gap-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 p-3 transition-colors"
                  >
                    <div className="w-24 h-16 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img
                        src={rec.cover}
                        alt={rec.title}
                        title={`${rec.title} - ${typeLabels[rec.type]}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rec.title}
                      </h3>
                      <span className="text-xs text-foreground-500 mt-1 inline-flex items-center gap-1">
                        <i className="ri-eye-line" /> {formatCount(rec.views)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </article>
      </main>
      <Footer />

      {playerOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground-950/60" onClick={() => setPlayerOpen(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-background-50 p-7 text-center">
            <span className="w-14 h-14 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto">
              <i className="ri-play-circle-line text-3xl" />
            </span>
            <h3 className="font-heading font-semibold text-lg text-foreground-950 mt-4">
              这里是演示播放器
            </h3>
            <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
              当前为前端演示版本，暂未接入真实视频流。正式上线后可播放《{item.title}》的完整视频。
            </p>
            <button
              type="button"
              onClick={() => setPlayerOpen(false)}
              className="mt-6 w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              知道啦
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm">
          {toast}
        </div>
      )}
    </div>
  );
}